import React, { useState } from "react";
import { Box, Button, Typography, Stack, Chip } from "@mui/material";
import { styled } from "@mui/system";
import {
  Users,
  Sparkles,
  Heart,
  Lightbulb,
  CookingPot,
  ArrowBigRight,
} from "lucide-react";
import FoodieCallLogo from "../media/foodie-call.png";
import HeroImage from "../media/hero-image.png";
import { useNavigate } from "react-router-dom";
import { shouldShowHowItWorks } from "./HowItWorks";
import Footer from "./Footer";
import InstallButton from "./InstallButton";

// === Animations ===
// const bounceSlow = keyframes`
//   0%, 100% { transform: translateY(0); }
//   50% { transform: translateY(-10px); }
// `;

// === Styled Components ===
const PageWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100%",
  width: "100%",
});

const MainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2.5),
  paddingBottom: 0,
}));

const TopSection = styled(Box)(({ theme }) => ({
  width: "100%",
  textAlign: "center",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const LogoWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  //   animation: `${bounceSlow} 3s ease-in-out infinite`,
}));

const LogoImage = styled("img")({
  width: 280,
  height: "auto",
  display: "block",
  margin: "0 auto",
});

const TitleImage = styled("img")(({ theme }) => ({
  width: "100%",
  maxWidth: 240,
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  maxWidth: 300,
  margin: "0 auto",
  marginTop: theme.spacing(5),
  marginBottom: theme.spacing(2),
  lineHeight: 1.55,
  textAlign: "center",
  fontSize: "1rem",
}));

const FeaturePills = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(4),
  justifyContent: "center",
  gap: theme.spacing(1),
}));

const FeatureChip = styled(Chip)(({ theme }) => ({
  fontWeight: 500,
  borderRadius: 999,
}));

const OrangeFeatureChip = styled(FeatureChip)(({ theme }) => ({
  backgroundColor: "rgba(254, 215, 170, 0.7)",
  color: "#9A3412",
  padding: theme.spacing(0.5, 1),
}));

const AmberFeatureChip = styled(FeatureChip)(({ theme }) => ({
  backgroundColor: "rgba(253, 230, 138, 0.7)",
  color: "#92400E",
  padding: theme.spacing(0.5, 1),
}));

const BottomSection = styled(Box)(({ theme }) => ({
  width: "100%",
  marginBottom: theme.spacing(4),
}));

const StartButton = styled(Button)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  fontWeight: 700,
  fontSize: "1.05rem",
  textTransform: "none",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
  background: "linear-gradient(90deg, #F97316 0%, #EF4444 100%)",
  color: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1.5),
  transition: "all 150ms ease-in-out",
  "&:hover": {
    boxShadow: "0px 8px 18px rgba(0,0,0,0.25)",
    background: "linear-gradient(90deg, #FB923C 0%, #F97373 100%)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "scale(0.97)",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
  },
}));

const ArrowIconWrapper = styled("span", {
  shouldForwardProp: (prop) => prop !== "hovered",
})(({ hovered }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 150ms ease-in-out",
  transform: hovered ? "translateX(4px)" : "translateX(0)",
}));

// === Component ===
const Home = () => {
  const [isHovering, setIsHovering] = useState(false);

  const navigate = useNavigate();

  const handleCreateSession = () => {
    if (shouldShowHowItWorks()) {
      // Force a hard navigation by using window.location
      window.location.href = "/how-it-works";
    } else {
      navigate("/setup");
    }
  };

  return (
    <PageWrapper>
      <MainContent>
        {/* Top Section */}
        <TopSection>
          <LogoWrapper>
            <LogoImage src={FoodieCallLogo} alt="Foodie Call Logo" />
          </LogoWrapper>

          <DescriptionText variant="body1">
            Vote together, match instantly, and finally agree on where to eat!
          </DescriptionText>

          <TitleImage src={HeroImage} alt="Stop Arguing About Dinner" />

          <FeaturePills direction="row" spacing={1.5} flexWrap="wrap">
            <OrangeFeatureChip
              icon={<Sparkles size={16} />}
              label="Instant matches"
            />
            <AmberFeatureChip
              icon={<Users size={16} />}
              label="Up to 4 people"
            />
            <AmberFeatureChip
              icon={<Heart size={16} />}
              label="Local favorites"
            />
            <OrangeFeatureChip
              icon={<Lightbulb size={16} />}
              label="Smart suggestions"
            />
          </FeaturePills>
        </TopSection>

        <Box sx={{ mt: 3, mb: 2, display: "flex", justifyContent: "center" }}>
          <InstallButton />
        </Box>

        {/* Bottom Section */}
        <BottomSection>
          <StartButton
            fullWidth
            startIcon={<CookingPot size={22} />}
            endIcon={
              <ArrowIconWrapper hovered={isHovering}>
                <ArrowBigRight size={22} />
              </ArrowIconWrapper>
            }
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onClick={handleCreateSession}
          >
            Let's Get Started
          </StartButton>

          {/* <SubtitleText variant="body2" align="center">
            Share a link with your dining partner to begin
          </SubtitleText> */}
        </BottomSection>
      </MainContent>

      <Footer />
    </PageWrapper>
  );
};

export default Home;
